import { useState } from "react";
import { AccountType, IApiRequest, IAutoTriggerFlow } from "@miplatform/types";
import { AutoTriggerFlowFormValues } from "./components/AutoTriggerFlow";
import { CreateAutoTriggerFormValues } from "./components/CreateAutoTriggerModal";
import {
  useAccounts,
  useAutoTriggerFlows,
  useOrderTemplates,
} from "../../../../hooks";
import { buildAutoTriggerFlows } from "./autoTrigger.utils";

export const useOrderAutoTriggerViewModel = () => {
  const {
    isLoading: isAutoTriggerLoading,
    autoTriggerFlows,
    setAutoTriggerFlows,
    enableBuilder,
    disableBuilder,
    createAutoTriggerFlow: createAutoTriggerFlowFn,
    deleteAutoTriggerFlow: deleteAutoTriggerFlowFn,
    updateAutoTriggerFlow: updateAutoTriggerFlowFn,
    getAutoTriggerFlows: getAutoTriggerFlowsFn,
  } = useAutoTriggerFlows();
  const {
    isLoading: isAccountsLoading,
    accounts,
    getAccounts,
  } = useAccounts({ accountTypes: [AccountType.Builder] });
  const {
    orderTemplates,
    isLoading: isOrderTemplatesLoading,
    getOrderTemplates,
  } = useOrderTemplates();
  const [activeTab, setActiveTab] = useState<string | undefined>();

  const getAutoTriggerFlows = () => {
    getAutoTriggerFlowsFn({
      onSuccess: (flows: IAutoTriggerFlow[]) =>
        setAutoTriggerFlows(
          flows.map(
            (flow) =>
              ({
                ...flow,
                dependencyMaps: buildAutoTriggerFlows(flow.dependencyMaps),
              } as IAutoTriggerFlow)
          )
        ),
    });
  };

  const updateAutoTriggerFlow = (
    data: AutoTriggerFlowFormValues,
    onSuccess?: (flow: IAutoTriggerFlow) => void
  ) => {
    const filteredDependencyMaps = data.dependencyMaps.filter(
      ({ principalId }) => principalId
    );

    updateAutoTriggerFlowFn({
      id: data.id,
      modifiedOn: data.modifiedOn,
      data: { dependencyMaps: filteredDependencyMaps },
      onSuccess: (updatedFlow: IAutoTriggerFlow) => {
        setAutoTriggerFlows((prev) => {
          return prev.map((flow) =>
            flow.id === data.id
              ? ({
                  ...updatedFlow,
                  dependencyMaps: buildAutoTriggerFlows(
                    updatedFlow.dependencyMaps
                  ),
                } as IAutoTriggerFlow)
              : flow
          );
        });
        onSuccess?.(updatedFlow);
      },
    });
  };

  const deleteAutoTriggerFlow = (data: IApiRequest, onSuccess?: () => void) => {
    deleteAutoTriggerFlowFn({
      ...data,
      onSuccess: () => {
        setActiveTab(autoTriggerFlows[0]?.id);
        setAutoTriggerFlows((prev) =>
          prev.filter((flow) => flow.id !== data.id)
        );
        onSuccess?.();
      },
    });
  };

  const createAutoTriggerFlow = (
    data: CreateAutoTriggerFormValues,
    onSuccess?: (flow: IAutoTriggerFlow) => void
  ) => {
    createAutoTriggerFlowFn({
      data,
      onSuccess: (newFlow: IAutoTriggerFlow) => {
        setActiveTab(newFlow.id);
        setAutoTriggerFlows((prev) => [
          ...prev,
          {
            ...newFlow,
            dependencyMaps: buildAutoTriggerFlows(newFlow.dependencyMaps),
          } as IAutoTriggerFlow,
        ]);
        onSuccess?.(newFlow);
      },
    });
  };

  const updateAutoTriggerBuilder = ({
    id,
    builderId,
    isEnabled,
    modifiedOn,
  }: IApiRequest & { builderId: string; isEnabled: boolean }) => {
    (isEnabled ? enableBuilder : disableBuilder)({
      id,
      builderId,
      modifiedOn,
      onSuccess: (updatedFlow: IAutoTriggerFlow) => {
        setAutoTriggerFlows((prev) =>
          prev.map((flow) =>
            flow.id === id
              ? ({
                  ...updatedFlow,
                  dependencyMaps: buildAutoTriggerFlows(
                    updatedFlow.dependencyMaps
                  ),
                } as IAutoTriggerFlow)
              : flow
          )
        );
      },
    });
  };

  return {
    isLoading:
      isAccountsLoading || isAutoTriggerLoading || isOrderTemplatesLoading,
    autoTriggerFlows,
    accounts,
    orderTemplates,
    activeTab,
    setActiveTab,
    getAutoTriggerFlows,
    getAccounts,
    getOrderTemplates,
    createAutoTriggerFlow,
    updateAutoTriggerFlow,
    deleteAutoTriggerFlow,
    updateAutoTriggerBuilder,
  };
};
